import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Text,
  IconButton,
  useColorModeValue,
  HStack,
  VStack,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Textarea,
} from '@chakra-ui/react';
import {
  format,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isToday,
  isSameDay,
  addWeeks,
  subWeeks,
} from 'date-fns';
import { FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useHabits, Habit, HabitProgress } from '../../../contexts/HabitContext';

interface HabitTrackerProps {
  habit: Habit;
}

const HabitTracker: React.FC<HabitTrackerProps> = ({ habit }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { progress, toggleHabitCompletion, fetchProgress } = useHabits();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const todayBgColor = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    const startDate = startOfWeek(currentDate);
    const endDate = endOfWeek(currentDate);
    fetchProgress(startDate, endDate);
  }, [currentDate, fetchProgress]);

  const days = eachDayOfInterval({
    start: startOfWeek(currentDate),
    end: endOfWeek(currentDate),
  });

  const handlePreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const handleNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const getProgressForDate = (date: Date): HabitProgress | undefined => {
    return progress[habit.id]?.find((p) =>
      isSameDay(new Date(p.date), date)
    );
  };

  const handleToggle = (date: Date) => {
    if (habit.frequency.type === 'weekly' && !habit.frequency.days?.includes(date.getDay())) {
      return; // Not a scheduled day
    }
    if (habit.frequency.type === 'monthly' && !habit.frequency.dates?.includes(date.getDate())) {
      return; // Not a scheduled date
    }

    setSelectedDate(date);
    const existingProgress = getProgressForDate(date);
    setNotes(existingProgress?.notes || '');
    onOpen();
  };

  const handleSave = async () => {
    if (selectedDate) {
      await toggleHabitCompletion(habit.id, selectedDate, notes);
      onClose();
    }
  };

  return (
    <Box
      bg={bgColor}
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
    >
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between">
          <IconButton
            aria-label="Previous week"
            icon={<FiChevronLeft />}
            onClick={handlePreviousWeek}
            size="sm"
          />
          <Text fontWeight="medium">
            {format(days[0], 'MMM d')} - {format(days[6], 'MMM d, yyyy')}
          </Text>
          <IconButton
            aria-label="Next week"
            icon={<FiChevronRight />}
            onClick={handleNextWeek}
            size="sm"
          />
        </HStack>

        <Grid templateColumns="repeat(7, 1fr)" gap={2}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Text
              key={day}
              fontSize="sm"
              fontWeight="medium"
              textAlign="center"
              color="gray.500"
            >
              {day}
            </Text>
          ))}

          {days.map((date) => {
            const isScheduled =
              habit.frequency.type === 'daily' ||
              (habit.frequency.type === 'weekly' &&
                habit.frequency.days?.includes(date.getDay())) ||
              (habit.frequency.type === 'monthly' &&
                habit.frequency.dates?.includes(date.getDate()));

            const dayProgress = getProgressForDate(date);
            const isCompleted = dayProgress?.completed;

            return (
              <Box
                key={date.toISOString()}
                p={2}
                bg={isToday(date) ? todayBgColor : undefined}
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
                opacity={isScheduled ? 1 : 0.5}
                cursor={isScheduled ? 'pointer' : 'not-allowed'}
                onClick={() => isScheduled && handleToggle(date)}
              >
                <VStack spacing={1}>
                  <Text fontSize="sm" textAlign="center">
                    {format(date, 'd')}
                  </Text>
                  {isScheduled && (
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      bg={isCompleted ? 'green.500' : 'transparent'}
                      border="2px solid"
                      borderColor={isCompleted ? 'green.500' : 'gray.300'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {isCompleted && <FiCheck color="white" size={12} />}
                    </Box>
                  )}
                </VStack>
              </Box>
            );
          })}
        </Grid>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about your progress..."
                rows={4}
              />
              <Button colorScheme="blue" onClick={handleSave}>
                Save Progress
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HabitTracker; 